import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AppEngineDefinitionDto } from '../../models/APIModels';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  styled,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import useAppUtils from '../../Utils/useAppUtils';
import { ApiHelper } from '../../common/ApiHelper';
import { InferenceEngineFileTypes } from '../../enums';
import { uploadFile } from '../../common/Upload';
import Loader from '../Loader';
import { useNavigate } from 'react-router-dom';
import clientUtils from '../../Utils/clientUtils';
import CodeEditorContainer from './CodeEditorContainer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EngineInfo from './EngineInfo';
import { yupResolver } from '@hookform/resolvers/yup';
import { ENGINE_STATUS, Package_Definition_File_Name } from '../../constants';
import { ENTRY_POINT_PY_TEXT } from '../../data/app-engine-data';
import CircularProgressBar from '../../common/CircularProgressBar';
import Progress from '../Loaders/Progress';
import { GrDeploy } from 'react-icons/gr';
import Button from '../Button';

const Item = styled(Box)(({ theme }) => ({
  //   backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  //   ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  height: '200px',
  border: '2px dotted',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}));

interface Iprops {
  engine: AppEngineDefinitionDto;
}

const schema = yup.object().shape({
  engineName: yup.string().required(),
  memoryRequirement: yup
    .number()
    .min(1000, 'A minimum of 1000 memory is required.')
    .max(10000, 'You can enter a maximum value of 10000.')
    .required(),
  storageRequirement: yup
    .number()
    .min(1000, 'A minimum of 1000 storage is required.')
    .max(10000, 'You can enter a maximum value of 10000.')
    .required(),
});

const EngineEditor = ({ engine }) => {
  const { alert, confirm } = useAppUtils();
  const [isSaving, setIsSaving] = useState(false);
  const [loadingText, setLoadingText] = useState('Please wait...');
  const navigate = useNavigate();
  const [lastChecksum, setLastChecksum] = useState('');
  const [expandedAccordions, setExpandedAccordions] = useState(['content-section']);
  const [progress, setProgress] = React.useState(0);
  const [engineStatus, setEngineStatus] = React.useState('Initiating');

  const engineFormValues = useForm<AppEngineDefinitionDto>({
    resolver: yupResolver(schema),
    defaultValues: useMemo(() => {
      // console.log('engine form changed', engine);
      return engine;
    }, [engine]),
  });
  const fileListRef = useRef([]);

  const {
    register,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = engineFormValues;

  const [fileList, setFileList] = useState([]);

  const accordionClicked = (panel) => {
    // console.log('panel', panel, expandedAccordions);
    if (panel === 'content-section' && !expandedAccordions.includes(panel)) {
      setExpandedAccordions(['content-section']);
    } else if (panel === 'content-section' && expandedAccordions.includes(panel)) {
      setExpandedAccordions(expandedAccordions.filter((p) => p !== panel));
    } else if (panel === 'editor-section' && expandedAccordions.includes(panel)) {
      setExpandedAccordions(expandedAccordions.filter((p) => p !== panel));
    } else if (panel === 'editor-section' && !expandedAccordions.includes(panel)) {
      setExpandedAccordions(['editor-section']);
    }
  };

  const onSave = async () => {
    let updatedEngine = getValues();
    try {
      setIsSaving(true);
      if (await onlyUpsertEngine()) {
        alert('Engine updated');
        setIsSaving(false);
        return;
      }
    } catch (e) {
      setIsSaving(false);
      console.error('Unable to save engine', e);
      alert('Unable to save engine', 'error');
    }
  };

  const uploadToS3 = async (files) => {
    const promises = files.map(async (f) => {
      const publicUploadLink = await getUploadLink(f.fileType, f.fileName);
      if (publicUploadLink && f.file) {
        await uploadFile(f.file, publicUploadLink);
      }
    });
    const result = await Promise.all(promises);
  };

  const getEngineStatus = async (engineId: string) => {
    try {
      const engine = await ApiHelper.GetEngineById(engineId);
      let engineStatus = '';
      if (engine && engine.data) {
        engineStatus = engine.data.status;
      }
      return engineStatus;
    } catch (e) {
      alert(`Unable to get engine for id ${engineId}`, 'error');
    }
  };

  // Upser Engine Status Checking ......
  const upsertEngineStatusCheck = (resp: any) => {
    try {
      return new Promise((resolve, reject) => {
        try {
          setProgress(20); // for showing some progress before we actually receive resp from apis
          const intervalId = setInterval(async () => {
            let progressVal = progress;
            const engineStatus = await getEngineStatus(engine.engineId);
            if (engineStatus) {
              setEngineStatus(engineStatus);
              switch (engineStatus) {
                case 'PreparingToUpdate':
                  progressVal = 25;
                  break;
                case 'InstallingPackages':
                  progressVal = 50;
                  break;
                case 'UpdatingImage':
                  progressVal = 75;
                  break;
                case 'Ready':
                  progressVal = 100;
                  break;
                default:
                  break;
              }
              setProgress(progressVal);
            }
            if (engineStatus === 'Ready') {
              clearInterval(intervalId);
              // wait for a sec to show the ready status on screen
              setTimeout(() => {
                setProgress(0);
                setEngineStatus('Initiating');
              }, 1000);
              reset(resp.data);
              setIsSaving(false);
              const newFileList: any = generateFileList();
              setFileList(newFileList);
              fileListRef.current = newFileList;
              resolve(true);
            }
          }, 5 * 1000);
        } catch (error) {
          reject(false);
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  // only upsert Engine ( does not check status) ......
  const onlyUpsertEngine = async () => {
    try {
      // getting all engine data ......
      const data = getValues();
      setLastChecksum(clientUtils.getChecksum(data));

      // setting platform and pkg defination file name ......
      data.appEnginePlatform = 'Python 3.8';
      data.packageDefinitionFileName = Package_Definition_File_Name;

      // upser api call ......
      const response = await ApiHelper.UpsertEngine(data);

      return response;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  // upsert engine with status checking ......
  const upsertEngine = async () => {
    setIsSaving(true);
    const data = getValues();
    setLastChecksum(clientUtils.getChecksum(data));
    try {
      data.appEnginePlatform = 'Python 3.8';
      data.packageDefinitionFileName = Package_Definition_File_Name;
      setProgress(10); // for showing some progress before we actually receive resp from apis
      const response = await ApiHelper.UpsertEngine(data);
      if (response && response.data) {
        setProgress(20); // for showing some progress before we actually receive resp from apis
        const intervalId = setInterval(async () => {
          let progressVal = progress;
          const engineStatus = await getEngineStatus(response.data.engineId);
          if (engineStatus) {
            setEngineStatus(engineStatus);
            switch (engineStatus) {
              case 'PreparingToUpdate':
                progressVal = 25;
                break;
              case 'InstallingPackages':
                progressVal = 50;
                break;
              case 'UpdatingImage':
                progressVal = 75;
                break;
              case 'Ready':
                progressVal = 100;
                break;
              default:
                break;
            }
            setProgress(progressVal);
          }
          if (engineStatus === 'Ready') {
            clearInterval(intervalId);
            // wait for a sec to show the ready status on screen
            setTimeout(() => {
              setIsSaving(false);
              setProgress(0);
              setEngineStatus('Initiating');
            }, 1000);
            reset(response.data);
            setIsSaving(false);
            const newFileList: any = generateFileList();
            setFileList(newFileList);
            fileListRef.current = newFileList;
            return true;
          }
        }, 5 * 1000);
      }
    } catch (e) {
      setIsSaving(false);
      setProgress(0);
      setEngineStatus('Initiating');
      console.error('Unable to save engine', e);
      return false;
    }
  };

  const getUploadLink = async (fileType, fileName) => {
    try {
      const { data, status } = await ApiHelper.GetUploadLinkForEngine(engine.engineId, fileType, fileName);
      if (status === 200 && data) {
        return data.publicUploadLink;
      }
    } catch (e) {
      console.error(`Unable to get upload info`, 'error');
      return null;
    }
  };

  const onCreateNewFile = async (value) => {
    try {
      setIsSaving(true);
      const parts = value.split('.');
      const fileName = `${parts[0].replace(' ', '-')}.py`;
      let oldFiles = fileListRef.current;
      const isAlreadyExits = oldFiles.find((f) => f.fileName === fileName);
      if (isAlreadyExits) {
        alert('File Name already exits', 'error');
        return;
      }
      const publicUploadLink = await getUploadLink(InferenceEngineFileTypes.SourceFile, fileName);
      if (publicUploadLink) {
        const file = new Blob([''], { type: 'text/plain' });
        await uploadFile(file, publicUploadLink);
        let newFiles = oldFiles.push({ fileName: fileName, fileType: InferenceEngineFileTypes.SourceFile });
        setValue('files', oldFiles);
        if (await upsertEngine()) {
          alert(`File Created.`);
        }
      }
      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
    }
  };

  const onUpload = async (obj) => {
    // console.log('obj', { obj });
    setLoadingText('Uploading file(s)....');
    setIsSaving(true);
    const fileType = obj.type;
    const newFiles = obj.files.map((file) => {
      return {
        fileType: fileType,
        fileName: file.name,
        status: 'Uploading',
        jobProgress: 0,
        id: uuidv4(),
        file: file,
      };
    });
    try {
      if (newFiles.length > 0) {
        await uploadToS3(newFiles);
      }
      let oldFiles = fileListRef.current;
      const mergedFiles = Object.values(
        [].concat(oldFiles, newFiles).reduce((r, c) => ((r[c.fileName] = Object.assign(r[c.fileName] || {}, c)), r), {})
      );
      setValue('files', mergedFiles);
      if (await upsertEngine()) {
        alert(`${newFiles.length} file(s) uploaded successfully.`);
        setLoadingText('Please wait....');
      }
      setIsSaving(false);
    } catch (e) {
      setLoadingText('Please wait....');
      setIsSaving(false);
    }
  };
  const onDeleteFile = async (file) => {
    const isOk = await confirm('Are you want to delete this file ?');
    if (isOk) {
      let files = fileListRef.current;
      files = files.filter((f) => f.fileName !== file.labelText);
      setValue('files', files);
      if (await upsertEngine()) {
        alert(`File Deleted.`);
      }
    }
  };

  const onSaveFileChanges = async (file, str) => {
    // console.log('onSaveFileChanges', { file, str });
    try {
      setIsSaving(true);
      const { fileName, type } = file;
      const publicUploadLink = await getUploadLink(type, fileName);
      if (publicUploadLink) {
        const file = new Blob([str], { type: 'text/plain' });
        await uploadFile(file, publicUploadLink);
        if (await onlyUpsertEngine()) {
          alert('File Saved!');
        }
      }
      setIsSaving(false);
    } catch (error) {
      alert('Unable to save file', 'error');
      console.error('Unable to save file', { error });
      setIsSaving(false);
    }
  };

  const generateFileList = () => {
    let newFileList = getValues('files');
    newFileList = newFileList.map((file) => {
      return {
        ...file,
        status: 'Uploaded',
        id: uuidv4(),
        jobProgress: 100,
      };
    });
    fileListRef.current = newFileList;
    return newFileList;
  };

  const handleCancel = async () => {
    const newChecksum = clientUtils.getChecksum(getValues());
    if (lastChecksum !== newChecksum) {
      const isOk = await confirm('Are you sure you want to cancel?  Any unsaved changes will be lost.');
      if (isOk) {
        navigate('/engines');
      }
    } else {
      navigate('/engines');
    }
  };

  const createEntryPointFile = async () => {
    const entryPointFile = {
      fileType: InferenceEngineFileTypes.SourceFile,
      fileName: 'EntryPoint.py',
    };
    const files = [
      {
        fileType: InferenceEngineFileTypes.SourceFile,
        fileName: 'EntryPoint.py',
      },
    ];
    setValue('files', files);
    const publicUploadLink = await getUploadLink(entryPointFile.fileType, entryPointFile.fileName);
    if (publicUploadLink) {
      const file = new Blob([ENTRY_POINT_PY_TEXT], { type: 'text/plain' });
      await uploadFile(file, publicUploadLink);
      await upsertEngine();
    }
    setIsSaving(false);
  };

  // deploy engine ......
  const deployEngine = async () => {
    try {
      const upsertEngineResponse = await onlyUpsertEngine();
      const { status } = await ApiHelper.DeployAppEngine(engine.engineId);

      if (upsertEngineResponse) {
        if (status === 200) {
          await upsertEngineStatusCheck(upsertEngineResponse);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    // console.log('engine', engine);
    if (engine) {
      if (engine.files.length === 0) {
        createEntryPointFile();
      }
      setFileList(generateFileList());
      setLastChecksum(clientUtils.getChecksum(getValues()));
    }
  }, [engine]);

  useEffect(() => {
    // console.log('fileList', fileList);
  }, [fileList]);
  return (
    <>
      <div className="content">
        {(isSaving || progress !==0) && 
          <div className='progressbar-container'>
            <div className={`progressbar-wrapper ${progress !==0 ? 'wrapper-padding' : ''}`}>
              <CircularProgressBar value={progress} />
              {progress !==0 && <p className='progress-label'>{ENGINE_STATUS[engineStatus]}</p>}
            </div>
          </div>
        }
        <FormProvider {...engineFormValues}>
          <form className="" onSubmit={engineFormValues.handleSubmit(onSave)}>
            <div className="engine-container">
              <Accordion
                onChange={() => accordionClicked('content-section')}
                expanded={expandedAccordions.includes('content-section')}
              >
                <AccordionSummary
                  className={expandedAccordions.includes('content-section') ? 'accordion-summary-custom' : ''} // {expanded === 'details-section' ? 'accordion-summary-custom' : ''}
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="content-section"
                  id="content-section"
                >
                  <div className="accordion-summary-title">Engine Details</div>
                </AccordionSummary>
                <AccordionDetails className="content-section">
                  <EngineInfo />
                  <div className="button-container">
                    <Box sx={{ flex: '1 1 auto', textAlign: 'right' }}>
                      <Button variant="contained" sx={{ marginRight: '3px' }} onClick={deployEngine} endIcon={<GrDeploy />}>
                        Deploy
                      </Button>
                      <Button variant="contained" style={{ marginRight: '3px' }} onClick={handleCancel}>
                        Cancel
                      </Button>

                      <Button variant="contained" onClick={engineFormValues.handleSubmit(onSave)}>
                        Save
                      </Button>
                    </Box>
                  </div>
                </AccordionDetails>
              </Accordion>
              <Accordion
                onChange={() => accordionClicked('editor-section')}
                expanded={expandedAccordions.includes('editor-section')}
              >
                <AccordionSummary
                  className={expandedAccordions.includes('editor-section') ? 'accordion-summary-custom' : ''} // {expanded === 'details-section' ? 'accordion-summary-custom' : ''}
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="editor-section"
                  id="editor-section"
                >
                  <div className="accordion-summary-title">Code Editor</div>
                </AccordionSummary>
                <AccordionDetails>
                  <CodeEditorContainer
                    onUpload={onUpload}
                    onDelete={onDeleteFile}
                    onCreateNewFile={onCreateNewFile}
                    onSaveFileChanges={onSaveFileChanges}
                  />
                  <div className="deploy-engine-btn-container">
                    <Button variant="contained" onClick={deployEngine} endIcon={<GrDeploy />}>
                      Deploy
                    </Button>
                  </div>
                </AccordionDetails>
              </Accordion>
            </div>
          </form>
        </FormProvider>
      </div>
    </>
  );
};

export default EngineEditor;
